/**
 * Super Admin → Indian Kanoon Configuration
 *
 * Mirrors the EcourtsConfig layout — placed as a separate page so we don't
 * disturb the existing eCourts page. Provides:
 *   • Master enable toggle + API token entry (masked once saved)
 *   • Per-endpoint pricing/markup overrides
 *   • One-click "Test token" (₹0.02 — not billed to any tenant)
 *   • Recent passbook audit (last 50 IK API calls)
 */
import { useEffect, useState } from "react";
import { Loader2, Globe2, KeyRound, BadgeIndianRupee, BarChart3, Activity } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Field, Input, GoldButton, EmptyState } from "@/components/Ui";
import PoweredByIKanoonBadge from "@/components/PoweredByIKanoonBadge";

export default function IndianKanoonConfig() {
    const [cfg, setCfg]       = useState(null);
    const [token, setToken]   = useState("");
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [pb, setPb]         = useState([]);

    const reload = async () => {
        try {
            const r = await api.get("/admin/indiankanoon/config");
            setCfg(r.data);
        } catch (e) {
            toast.error(apiError(e, "Could not load Indian Kanoon config."));
        }
    };

    const reloadPassbook = async () => {
        try {
            const r = await api.get("/admin/indiankanoon/passbook?limit=50");
            setPb(r.data || []);
        } catch (_e) {
            // best-effort — passbook is non-critical
        }
    };

    useEffect(() => { reload(); reloadPassbook(); }, []);

    const saveMaster = async (patch) => {
        setSaving(true);
        try {
            const r = await api.patch("/admin/indiankanoon/config", patch);
            setCfg(r.data);
            toast.success("Saved.");
        } catch (e) {
            toast.error(apiError(e, "Save failed."));
        } finally {
            setSaving(false);
        }
    };

    const saveToken = async () => {
        if (!token.trim()) { toast.error("Paste the API token first."); return; }
        await saveMaster({ token: token.trim() });
        setToken("");
    };

    const test = async () => {
        setTesting(true);
        try {
            const r = await api.post("/admin/indiankanoon/test", {});
            toast.success(`Token works! Test doc: "${r.data.title || "loaded"}"`);
        } catch (e) {
            toast.error(apiError(e, "Token test failed."));
        } finally {
            setTesting(false);
        }
    };

    const saveEndpoint = async (key, patch) => {
        try {
            await api.patch(`/admin/indiankanoon/endpoints/${key}`, patch);
            reload();
            toast.success("Endpoint updated.");
        } catch (e) {
            toast.error(apiError(e, "Endpoint update failed."));
        }
    };

    if (!cfg) {
        return (
            <div className="flex items-center gap-2 text-sm text-zinc-500 py-10">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="indiankanoon-config-page">
            <PageHeader
                title="Indian Kanoon"
                subtitle="Hybrid precedent-search provider. Pay-per-call, ₹500 free credit on signup. Pricing & markup configurable below."
            />

            {/* Attribution + compliance summary card. Doubles as the
                "About / Documentation" surface required by the IK ToS
                when our generated AI headnotes use IK data via RAG. */}
            <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-5">
                <PoweredByIKanoonBadge placement="inline" className="mb-3"/>
                <p className="text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed">
                    All legal precedents and document fragments displayed across this product —
                    including the AI-generated IRAC headnotes — are sourced from <a href="https://indiankanoon.org" target="_blank" rel="noopener noreferrer" className="text-primary underline">Indian Kanoon</a>'s
                    public collection of Indian court records, retrieved via their <a href="https://api.indiankanoon.org" target="_blank" rel="noopener noreferrer" className="text-primary underline">official API</a>.
                    Per Indian Kanoon's <a href="https://api.indiankanoon.org/terms/" target="_blank" rel="noopener noreferrer" className="text-primary underline">Terms &amp; Conditions</a>,
                    data is provided <strong>"as is"</strong> without warranty of accuracy, reliability, or
                    fitness for any particular purpose. Always verify against the official certified
                    copy before relying on any precedent in court. Your IK API token below is
                    stored encrypted at rest using AES-128-CBC + HMAC (Fernet).
                </p>
            </div>

            {/* Master config card */}
            <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-5 space-y-4">
                <div className="flex items-center gap-2">
                    <Globe2 className="w-4 h-4 text-indigo-700 dark:text-indigo-300" />
                    <div className="text-sm font-semibold">Integration</div>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox"
                               checked={!!cfg.enabled}
                               onChange={(e) => saveMaster({ enabled: e.target.checked })}
                               disabled={saving}
                               data-testid="ik-enabled-toggle" />
                        Enable Indian Kanoon integration
                    </label>
                    {cfg.token_set && (
                        <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                            Token configured · {cfg.token_masked}
                        </span>
                    )}
                </div>

                <Field label="API Token" hint="Get yours at api.indiankanoon.org → My Account. You get ₹500 free credit on signup.">
                    <div className="flex gap-2">
                        <div className="flex-1">
                            <Input
                                value={token}
                                onChange={(e) => setToken(e.target.value)}
                                placeholder={cfg.token_set ? "Replace existing token (leave empty to keep)" : "Paste your IK API token"}
                                data-testid="ik-token-input"
                                type="password"
                            />
                        </div>
                        <GoldButton onClick={saveToken} disabled={saving || !token.trim()} data-testid="ik-token-save">
                            <KeyRound className="w-4 h-4" /> Save token
                        </GoldButton>
                        <button
                            onClick={test}
                            disabled={testing || !cfg.token_set}
                            data-testid="ik-token-test"
                            className="px-4 py-2 text-sm border border-zinc-300 dark:border-zinc-700 rounded-lg hover:border-primary disabled:opacity-50"
                        >
                            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                            Test
                        </button>
                    </div>
                </Field>

                <Field label="Base URL (advanced)">
                    <Input
                        value={cfg.base_url}
                        onChange={(e) => setCfg({ ...cfg, base_url: e.target.value })}
                        onBlur={() => saveMaster({ base_url: cfg.base_url })}
                        placeholder="https://api.indiankanoon.org"
                    />
                </Field>
            </div>

            {/* Endpoint pricing table */}
            <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 overflow-hidden">
                <div className="p-5 border-b border-zinc-200 dark:border-zinc-800 flex items-center gap-2">
                    <BadgeIndianRupee className="w-4 h-4 text-indigo-700 dark:text-indigo-300" />
                    <div className="text-sm font-semibold">Endpoint pricing & markup</div>
                    <span className="ml-auto text-xs text-zinc-500">Default markup: {cfg.default_markup_percent}%</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-zinc-50 dark:bg-zinc-800/50 text-xs uppercase tracking-wider text-zinc-600 dark:text-zinc-300">
                            <tr>
                                <th className="text-left px-4 py-2">Endpoint</th>
                                <th className="text-right px-4 py-2">Base (₹)</th>
                                <th className="text-right px-4 py-2">Markup %</th>
                                <th className="text-right px-4 py-2">Add-on (₹)</th>
                                <th className="text-right px-4 py-2">Tenant pays</th>
                                <th className="text-center px-4 py-2">Enabled</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(cfg.endpoints || []).map((ep) => {
                                const tenantPays = (ep.base_cost_inr * (1 + ep.markup_percent/100) + ep.fixed_addon_inr).toFixed(2);
                                return (
                                    <EndpointRow key={ep.key} ep={ep} tenantPays={tenantPays}
                                                 onSave={(p) => saveEndpoint(ep.key, p)} />
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Passbook */}
            <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800">
                <div className="p-5 border-b border-zinc-200 dark:border-zinc-800 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-indigo-700 dark:text-indigo-300" />
                    <div className="text-sm font-semibold">Recent Indian Kanoon API calls</div>
                    <button onClick={reloadPassbook} className="ml-auto text-xs underline text-zinc-500 hover:text-primary">
                        Refresh
                    </button>
                </div>
                {pb.length === 0 ? (
                    <div className="p-6">
                        <EmptyState
                            title="No Indian Kanoon calls yet"
                            subtitle="Once tenants run a precedent search, you'll see audit entries here."
                        />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-zinc-50 dark:bg-zinc-800/50 text-xs uppercase tracking-wider text-zinc-600 dark:text-zinc-300">
                                <tr>
                                    <th className="text-left px-4 py-2">When</th>
                                    <th className="text-left px-4 py-2">Endpoint</th>
                                    <th className="text-left px-4 py-2">Tenant</th>
                                    <th className="text-left px-4 py-2">User</th>
                                    <th className="text-left px-4 py-2">Status</th>
                                    <th className="text-right px-4 py-2">Billed</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pb.map((r) => (
                                    <tr key={r.id} className="border-t border-zinc-200 dark:border-zinc-800">
                                        <td className="px-4 py-2 text-xs">{(r.created_at || "").replace("T", " ").slice(0, 16)}</td>
                                        <td className="px-4 py-2">{r.endpoint_label}</td>
                                        <td className="px-4 py-2 text-xs text-zinc-500">{r.tenant_id}</td>
                                        <td className="px-4 py-2 text-xs text-zinc-500">{r.user_email}</td>
                                        <td className="px-4 py-2">
                                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                r.status === "ok"
                                                    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                                    : "bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                                            }`}>{r.status}</span>
                                        </td>
                                        <td className="px-4 py-2 text-right">₹{r.billed_inr?.toFixed?.(2) ?? r.billed_inr}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function EndpointRow({ ep, tenantPays, onSave }) {
    const [base, setBase]     = useState(ep.base_cost_inr);
    const [markup, setMarkup] = useState(ep.markup_percent);
    const [addon, setAddon]   = useState(ep.fixed_addon_inr);

    useEffect(() => { setBase(ep.base_cost_inr); setMarkup(ep.markup_percent); setAddon(ep.fixed_addon_inr); }, [ep]);

    return (
        <tr className="border-t border-zinc-200 dark:border-zinc-800">
            <td className="px-4 py-2">
                <div className="font-medium text-zinc-900 dark:text-zinc-100">{ep.label}</div>
                <div className="text-xs text-zinc-500">{ep.description}</div>
            </td>
            <td className="px-4 py-2 text-right">
                <input
                    type="number"
                    step="0.01"
                    value={base}
                    onChange={(e) => setBase(parseFloat(e.target.value || 0))}
                    onBlur={() => base !== ep.base_cost_inr && onSave({ base_cost_inr: base })}
                    className="w-20 text-right px-2 py-1 rounded border border-zinc-300 dark:border-zinc-700 bg-transparent"
                    data-testid={`ik-base-${ep.key}`}
                />
            </td>
            <td className="px-4 py-2 text-right">
                <input
                    type="number"
                    step="1"
                    value={markup}
                    onChange={(e) => setMarkup(parseFloat(e.target.value || 0))}
                    onBlur={() => markup !== ep.markup_percent && onSave({ markup_percent: markup })}
                    className="w-20 text-right px-2 py-1 rounded border border-zinc-300 dark:border-zinc-700 bg-transparent"
                    data-testid={`ik-markup-${ep.key}`}
                />
            </td>
            <td className="px-4 py-2 text-right">
                <input
                    type="number"
                    step="0.01"
                    value={addon}
                    onChange={(e) => setAddon(parseFloat(e.target.value || 0))}
                    onBlur={() => addon !== ep.fixed_addon_inr && onSave({ fixed_addon_inr: addon })}
                    className="w-20 text-right px-2 py-1 rounded border border-zinc-300 dark:border-zinc-700 bg-transparent"
                    data-testid={`ik-addon-${ep.key}`}
                />
            </td>
            <td className="px-4 py-2 text-right font-semibold">₹{tenantPays}</td>
            <td className="px-4 py-2 text-center">
                <input
                    type="checkbox"
                    checked={ep.enabled}
                    onChange={(e) => onSave({ enabled: e.target.checked })}
                    data-testid={`ik-enabled-${ep.key}`}
                />
            </td>
        </tr>
    );
}
